/**
 * offlineCache — backup leve de drafts em IndexedDB para recuperação
 * quando o backend falhar (rede caiu, 5xx, etc.).
 *
 * Estratégia: ao detectar erro de PUT no draftStore, gravamos o último
 * metadata por projectId. No próximo bootstrap do editor, se houver
 * backup mais novo que o draft remoto, oferecemos recuperar.
 *
 * Não usa libs externas — só a IndexedDB API nativa.
 */

import type { ProjectMetadataV1 } from '@/services/metadata/schema'

const DB_NAME = 'morpherd_offline_cache'
const DB_VERSION = 1
const STORE = 'drafts'

export interface CachedDraftEntry {
  projectId: number
  baseCommitId: number | null
  metadata: ProjectMetadataV1
  /** ISO timestamp do momento do cache. */
  cachedAt: string
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB indisponível neste ambiente'))
  }
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'projectId' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('Falha ao abrir IndexedDB'))
  })
  return dbPromise
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode)
    const req = fn(tx.objectStore(STORE))
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('Erro IndexedDB'))
  })
}

export async function saveDraftBackup(entry: Omit<CachedDraftEntry, 'cachedAt'>): Promise<void> {
  try {
    const full: CachedDraftEntry = { ...entry, cachedAt: new Date().toISOString() }
    await withStore('readwrite', (s) => s.put(full))
  } catch {
    /* best-effort */
  }
}

export async function getDraftBackup(projectId: number): Promise<CachedDraftEntry | null> {
  try {
    const result = await withStore<CachedDraftEntry | undefined>('readonly', (s) => s.get(projectId))
    return result ?? null
  } catch {
    return null
  }
}

export async function clearDraftBackup(projectId: number): Promise<void> {
  try {
    await withStore('readwrite', (s) => s.delete(projectId))
  } catch {
    /* best-effort */
  }
}

export async function clearAllDraftBackups(): Promise<void> {
  try {
    await withStore('readwrite', (s) => s.clear())
  } catch {
    /* best-effort */
  }
}
